/**
 * Floating name labels for ghosts.
 *
 * A name is drawn once into a 256×64 canvas, uploaded as a `CanvasTexture` and
 * reused by every sprite that shows that name — the texture is the expensive
 * part, the sprite is not. Textures are refcounted in a module-level cache, so
 * two players called `bones` cost one upload, and the GPU memory comes back when
 * the last of them leaves.
 *
 * The material is *not* shared: opacity is per-sprite (stale and distant ghosts
 * fade independently), so each tag clones its own `SpriteMaterial`.
 */

import { CanvasTexture, LinearFilter, Sprite, SpriteMaterial, SRGBColorSpace, type Texture } from 'three';

/** Label texture size. Wide enough for `MAX_NAME_LEN` glyphs at a readable size. */
const TEX_W = 256;
const TEX_H = 64;

/** World-space sprite size in metres. Roughly 1.6 m wide, matching a skull-ish scale. */
const SPRITE_W = 1.6;
const SPRITE_H = 0.4;

const PILL_FILL = 'rgba(10, 12, 17, 0.72)';
const PILL_STROKE = 'rgba(237, 230, 210, 0.22)';
const TEXT_FILL = '#f2ede1';
const FONT_STACK = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

/** One cached texture plus how many live tags reference it. */
interface CacheEntry {
  tex: Texture;
  refs: number;
}

const cache = new Map<string, CacheEntry>();

/** A name label you can position, fade and release. */
export interface NameTag {
  /** Add this to the scene (or to a rig root). Positioning is the caller's job. */
  sprite: Sprite;
  /** `0` hides the sprite outright, so fully faded tags cost no draw call. */
  setOpacity(o: number): void;
  /** Release the material and drop this tag's reference on the shared texture. */
  dispose(): void;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawTexture(name: string): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = TEX_W;
  canvas.height = TEX_H;
  const ctx = canvas.getContext('2d');

  if (ctx !== null) {
    ctx.clearRect(0, 0, TEX_W, TEX_H);

    // Pill background, inset so the rounded edge is not clipped by the texture border.
    const pad = 4;
    roundRect(ctx, pad, pad, TEX_W - pad * 2, TEX_H - pad * 2, (TEX_H - pad * 2) / 2);
    ctx.fillStyle = PILL_FILL;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = PILL_STROKE;
    ctx.stroke();

    // Shrink the font until the name fits the pill's inner width.
    const maxTextW = TEX_W - 40;
    let size = 30;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = TEXT_FILL;
    do {
      ctx.font = `bold ${size}px ${FONT_STACK}`;
      if (ctx.measureText(name).width <= maxTextW) break;
      size -= 2;
    } while (size > 12);

    ctx.fillText(name, TEX_W / 2, TEX_H / 2 + 1, maxTextW);
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

function acquire(name: string): Texture {
  let entry = cache.get(name);
  if (entry === undefined) {
    entry = { tex: drawTexture(name), refs: 0 };
    cache.set(name, entry);
  }
  entry.refs++;
  return entry.tex;
}

function release(name: string): void {
  const entry = cache.get(name);
  if (entry === undefined) return;
  entry.refs--;
  if (entry.refs <= 0) {
    entry.tex.dispose();
    cache.delete(name);
  }
}

/**
 * Build a name label. Cheap after the first tag with the same text.
 *
 * `depthTest` stays on so tags are correctly occluded by the mountain — a name
 * floating through 30 m of rock reads as a bug, not as information.
 */
export function createNameTag(name: string): NameTag {
  const tex = acquire(name);
  const material = new SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    sizeAttenuation: true,
    opacity: 1,
  });
  const sprite = new Sprite(material);
  sprite.scale.set(SPRITE_W, SPRITE_H, 1);
  sprite.renderOrder = 2;

  let disposed = false;

  return {
    sprite,
    setOpacity(o: number): void {
      if (disposed) return;
      const clamped = o < 0 ? 0 : o > 1 ? 1 : o;
      if (material.opacity !== clamped) material.opacity = clamped;
      const visible = clamped > 0.01;
      if (sprite.visible !== visible) sprite.visible = visible;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      sprite.removeFromParent();
      material.dispose();
      release(name);
    },
  };
}

/** Number of distinct cached label textures. Diagnostics only. */
export function nameTagCacheSize(): number {
  return cache.size;
}
